/**
 * Service worker registration + `beforeinstallprompt` helper.
 * Keep it tiny: no workbox, no PWA CLI. Just a vanilla register + a hook
 * that exposes the deferred install prompt for the InstallPrompt UI.
 */

export function registerServiceWorker() {
    if (typeof window === "undefined") return;
    // Only register in production to avoid caching the CRA dev bundle.
    const isDev = process.env.NODE_ENV !== "production";
    if (isDev || !("serviceWorker" in navigator)) return;
    window.addEventListener("load", () => {
        navigator.serviceWorker
            .register("/service-worker.js")
            .catch((err) => {
                // Non-fatal — log and move on.
                // eslint-disable-next-line no-console
                console.warn("SW registration failed:", err);
            });
    });

    // When a new SW takes control (e.g. we bumped CACHE_VERSION), the
    // currently-open tab is still running the old bundle. Reload once so
    // the fresh code is picked up. `sessionStorage` guards against a
    // reload loop if something goes wrong.
    navigator.serviceWorker.addEventListener("message", (event) => {
        if (event.data?.type !== "sw-updated") return;
        if (sessionStorage.getItem("vm_sw_reloaded") === "1") return;
        sessionStorage.setItem("vm_sw_reloaded", "1");
        window.location.reload();
    });
}

let deferredPrompt = null;
const listeners = new Set();

function notifyListeners(available) {
    listeners.forEach((fn) => {
        try {
            fn(available);
        } catch (err) {
            // One bad listener mustn't poison the rest. Log so it's
            // still visible during debugging.
            // eslint-disable-next-line no-console
            console.warn("InstallPrompt listener threw:", err);
        }
    });
}

if (typeof window !== "undefined") {
    window.addEventListener("beforeinstallprompt", (e) => {
        e.preventDefault();
        deferredPrompt = e;
        notifyListeners(true);
    });
    window.addEventListener("appinstalled", () => {
        deferredPrompt = null;
        notifyListeners(false);
    });
}

export function onInstallPromptChange(fn) {
    listeners.add(fn);
    // Fire immediately with current state
    fn(Boolean(deferredPrompt));
    return () => listeners.delete(fn);
}

export async function triggerInstallPrompt() {
    if (!deferredPrompt) return { outcome: "unavailable" };
    const promptEvent = deferredPrompt;
    deferredPrompt = null;
    notifyListeners(false);
    promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    return choice;
}
